var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DailyTimelineTooltipLine } from '@web/components/layout/WidgetTooltips/DailyTimelineTooltip';
import { differenceInMinutes, set } from 'date-fns';
var GROUP_THRESHOLD_MINUTES = 3;
var TOOLTIP_GROUP_THRESHOLD_MINUTES = 5;
var MAX_TOOLTIP_ITEMS = 10;
var AVATAR_GROUP_THRESHOLD = 20;
var MAX_AVATARS_PER_SEGMENT = 3;
var parseWorkingClock = function (time) {
    var _a = time.split(':').map(Number), hours = _a[0], minutes = _a[1];
    return { hours: hours, minutes: minutes };
};
export function getFullDayTimelineRange(items, workingHours) {
    var _a = parseWorkingClock(workingHours.startWorkingHour), startWorkingHour = _a.hours, startWorkingMinute = _a.minutes;
    var _b = parseWorkingClock(workingHours.endWorkingHour), endWorkingHour = _b.hours, endWorkingMinute = _b.minutes;
    if (items.length === 0) {
        var today = new Date();
        return {
            startDate: set(today, {
                hours: startWorkingHour,
                minutes: startWorkingMinute,
                seconds: 0,
                milliseconds: 0
            }),
            endDate: set(today, {
                hours: endWorkingHour,
                minutes: endWorkingMinute,
                seconds: 0,
                milliseconds: 0
            })
        };
    }
    var sortedItems = __spreadArray([], items, true).sort(function (a, b) { return a.startDate.getTime() - b.startDate.getTime(); });
    var firstItem = sortedItems[0];
    var lastItem = sortedItems[sortedItems.length - 1];
    var firstEventDate = firstItem.startDate;
    var lastEventDate = lastItem.endDate || lastItem.startDate;
    var dayDate = firstEventDate;
    var workingStart = set(dayDate, {
        hours: startWorkingHour,
        minutes: startWorkingMinute,
        seconds: 0,
        milliseconds: 0
    });
    var workingEnd = set(dayDate, {
        hours: endWorkingHour,
        minutes: endWorkingMinute,
        seconds: 0,
        milliseconds: 0
    });
    return {
        startDate: firstEventDate < workingStart ? firstEventDate : workingStart,
        endDate: lastEventDate > workingEnd ? lastEventDate : workingEnd
    };
}
export function getStartEndWorkingHours(items, workingHours) {
    var _a;
    var sortedItems = __spreadArray([], items, true).sort(function (a, b) { return a.startDate.getTime() - b.startDate.getTime(); });
    var firstDate = (_a = sortedItems[0]) === null || _a === void 0 ? void 0 : _a.startDate;
    var lastItem = sortedItems[sortedItems.length - 1];
    var lastDate = (lastItem === null || lastItem === void 0 ? void 0 : lastItem.endDate) || (lastItem === null || lastItem === void 0 ? void 0 : lastItem.startDate);
    var _b = parseWorkingClock(workingHours.startWorkingHour), startWorkingHour = _b.hours, startWorkingMinute = _b.minutes;
    var _c = parseWorkingClock(workingHours.endWorkingHour), endWorkingHour = _c.hours, endWorkingMinute = _c.minutes;
    var workingSchedule = {
        startDate: set(firstDate !== null && firstDate !== void 0 ? firstDate : new Date(), {
            hours: startWorkingHour,
            minutes: startWorkingMinute,
            seconds: 0,
            milliseconds: 0
        }),
        endDate: set(lastDate !== null && lastDate !== void 0 ? lastDate : new Date(), {
            hours: endWorkingHour,
            minutes: endWorkingMinute,
            seconds: 0,
            milliseconds: 0
        })
    };
    return {
        startDate: firstDate && firstDate < workingSchedule.startDate
            ? firstDate
            : workingSchedule.startDate,
        endDate: lastDate && lastDate > workingSchedule.endDate
            ? lastDate
            : workingSchedule.endDate
    };
}
export function groupTimelineItems(items, thresholdMinutes, groupByType, getGroupKey) {
    if (groupByType === void 0) { groupByType = true; }
    if (items.length === 0)
        return [];
    var threshold = thresholdMinutes !== null && thresholdMinutes !== void 0 ? thresholdMinutes : GROUP_THRESHOLD_MINUTES;
    var sortedItems = __spreadArray([], items, true).sort(function (a, b) { return a.startDate.getTime() - b.startDate.getTime(); });
    var groups = [];
    var currentGroup = [sortedItems[0]];
    for (var i = 1; i < sortedItems.length; i++) {
        var currentItem = sortedItems[i];
        var lastItemInGroup = currentGroup[currentGroup.length - 1];
        var timeDiff = differenceInMinutes(currentItem.startDate, lastItemInGroup.startDate);
        var sameType = currentItem.type === lastItemInGroup.type;
        var sameGroupKey = !getGroupKey || getGroupKey(currentItem) === getGroupKey(lastItemInGroup);
        var withinThreshold = timeDiff <= threshold;
        var shouldGroup = groupByType
            ? withinThreshold && sameType && sameGroupKey
            : withinThreshold && sameGroupKey;
        if (shouldGroup) {
            currentGroup.push(currentItem);
        }
        else {
            groups.push(__spreadArray([], currentGroup, true));
            currentGroup = [currentItem];
        }
    }
    if (currentGroup.length > 0) {
        groups.push(currentGroup);
    }
    return groups;
}
export function getAvatarDisplayOptions(showAvatars) {
    if (!showAvatars) {
        return {
            groupThreshold: undefined,
            maxAvatarsPerSegment: MAX_AVATARS_PER_SEGMENT
        };
    }
    return {
        groupThreshold: AVATAR_GROUP_THRESHOLD,
        maxAvatarsPerSegment: MAX_AVATARS_PER_SEGMENT
    };
}
export function getUniqueUsersFromGroup(group) {
    var seen = new Set();
    return group
        .flatMap(function (item) { var _a; return (_a = item.users) !== null && _a !== void 0 ? _a : []; })
        .filter(function (u) {
        if (seen.has(u.id))
            return false;
        seen.add(u.id);
        return true;
    });
}
function groupTooltipItems(items) {
    if (!items.length)
        return [];
    return __spreadArray([], items, true).sort(function (a, b) { return a.startDate.getTime() - b.startDate.getTime(); })
        .reduce(function (groups, item) {
        var currentGroup = groups[groups.length - 1];
        if (!currentGroup) {
            groups.push([item]);
            return groups;
        }
        var lastItem = currentGroup[currentGroup.length - 1];
        var shouldGroup = item.title === lastItem.title &&
            item.type === lastItem.type &&
            differenceInMinutes(item.startDate, lastItem.startDate) <=
                TOOLTIP_GROUP_THRESHOLD_MINUTES;
        if (shouldGroup) {
            currentGroup.push(item);
        }
        else {
            groups.push([item]);
        }
        return groups;
    }, []);
}
export function createGroupTooltip(group, t) {
    if (!group.length)
        return null;
    var tooltipGroups = groupTooltipItems(group);
    if (tooltipGroups.length === 1) {
        return _jsx(DailyTimelineTooltipLine, { items: tooltipGroups[0] });
    }
    return (_jsxs("div", __assign({ className: "flex w-max flex-col gap-0.5" }, { children: [tooltipGroups.slice(0, MAX_TOOLTIP_ITEMS).map(function (items, index) { return (_jsx("div", __assign({ "data-testid": "tooltip-item" }, { children: _jsx(DailyTimelineTooltipLine, { items: items }) }), index)); }), tooltipGroups.length > MAX_TOOLTIP_ITEMS && (_jsx("div", __assign({ "data-testid": "more-events", className: "text-gray-500" }, { children: t('app.workPatterns.timeline.moreEvents', {
                    count: tooltipGroups.length - MAX_TOOLTIP_ITEMS
                }) })))] })));
}
